import React, { useEffect, useMemo, useRef } from 'react';
import { goBack, push } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'utils/modifiedTranslation';
import app from 'new-ui/app';
import { useFetchArticles } from 'components/Articles/useFetchArticles';
import { ArticleCard, CARD_THEMES } from 'components/Articles/Components';
import { useOpenArticleInIframe } from 'components/Articles/useOpenArticleInIframe';
import { ARTICLES_GA } from 'components/Articles/constants';
import { Skeleton } from 'components/Skeleton';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
import { ARTICLE_IN_IFRAME_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { useUpdateLocationSearchParameters } from 'utils/hooks/useUpdateLocationSearchParameters';
import { usePreliminaryAuthUser } from 'utils/hooks/usePreliminaryAuthUser';
import WidgetBtn from '../Button';
import AssistanceButton from '../AssistanceButton';
import styles from './ArticlesWidget.module.css';
import { FEED_GA } from '../constants';

export const ArticlesWidget = () => {
  const widgetRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const pushLocationSearchParameters = useUpdateLocationSearchParameters();

  const scrollToWidget = () => {
    if (widgetRef.current) {
      widgetRef.current.scrollIntoView();
    }
  };

  const { condition: userCondition } = usePreliminaryAuthUser() ?? {};

  const {
    articles,
    isLoaded,
  } = useFetchArticles(userCondition, 100);

  const randomArticles = useMemo(() => [...articles]
    .sort(() => 0.5 - Math.random())
    .slice(0, 2), [articles]);

  useEffect(() => {
    app.sendGoogleAnalyticsEvent(FEED_GA.category, FEED_GA.events.articlesViewed, {
      widget: 'articles',
    });
  }, []);

  const openArticleInIframe = useOpenArticleInIframe({
    onClose: () => {
      dispatch(goBack());
    },
  });

  return (
    <div className={styles.root} ref={widgetRef}>
      <div className={styles.header}>
        {t('articles_widget.title')}
      </div>
      {isLoaded ? (
        <div className={styles.body}>
          {randomArticles.map((article) => (
            <ArticleCard
              theme={CARD_THEMES.secondary}
              selectedTags={[]}
              key={article.id}
              item={article}
              onClick={() => {
                pushLocationSearchParameters({ [ARTICLE_IN_IFRAME_URL_SEARCH_PARAM]: article.sourceUrl });
                openArticleInIframe(article.sourceUrl);
                scrollToWidget();
              }}
            />
          ))}
        </div>
      ) : (
        <Skeleton width="100%" height="358px" />
      )}
      <div className={styles.footer}>
        <WidgetBtn
          title={t('articles_widget.actions.view_articles.button')}
          onClick={() => {
            app.sendGoogleAnalyticsEvent(ARTICLES_GA.category, ARTICLES_GA.events.articleTabView);
            dispatch(push(ROUTE_PATH[ROUTE_NAME.ARTICLES]));
          }}
        />
        <AssistanceButton
          title={t('articles_widget.actions.assistance.button')}
          onClick={() => {
            app.cache.assistTrigBy = 'articles';
          }}
        />
      </div>
    </div>
  );
};
